import { useEffect, useMemo, useRef, useState } from 'react';
import { GUIDE_INDEX, searchGuide } from './guideData';
import styles from './GuideSearch.module.css';

/**
 * Help Center search.
 *
 * Renders twice on the guide page: the large one in the hero for arrival, and a compact one
 * that fades into the sticky nav once the hero search scrolls away, so search stays reachable
 * from anywhere on a long page. GuidePage decides which of the two is showing.
 *
 * On focus it opens a panel listing every topic on the page, so a user can jump to a section
 * by clicking its title without typing. Typing filters that list. Each row links to its
 * section anchor, so selecting one scrolls straight to it.
 *
 * Props:
 *   idPrefix   keeps the results panel id unique, since two instances share one page.
 *   compact    the in-nav variant: shorter field, right-anchored panel, icon-only on mobile.
 *   hotkey     owns Ctrl/Cmd+K. Only ever passed to whichever instance is currently visible.
 *   scrollRoot ref to the page's scroll container. Required for the in-nav copy; see
 *              `handleChange` for what goes wrong without it.
 */
export default function GuideSearch({
  idPrefix = 'guide-search',
  compact = false,
  hotkey = false,
  scrollRoot = null,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const trimmed = query.trim();
  const isSearching = trimmed.length > 0;
  const panelId = `${idPrefix}-results`;

  // Empty query => show all topics; typing => filter them.
  const results = useMemo(
    () => (isSearching ? searchGuide(trimmed) : GUIDE_INDEX),
    [isSearching, trimmed],
  );

  // Ctrl/Cmd+K focuses search, which is what anyone who uses documentation expects. Bound on
  // one instance at a time so the two copies can never fight over the shortcut.
  useEffect(() => {
    if (!hotkey) return undefined;
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey]);

  // Safety net behind the real fix (the nav is fixed rather than sticky, so the browser has
  // no reason to scroll when the caret moves). Should anything still nudge the page while
  // typing, put the scroll position back.
  //
  // The page scrolls smoothly, so a stray scroll is an animation running over several frames
  // rather than a single jump: one correction would simply be overridden by the next frame of
  // it. Correct across a few frames instead, and use scrollTo, which cancels an in-flight
  // smooth scroll rather than queueing behind it. The window is ~4 frames, far too short to
  // interfere with someone deliberately scrolling.
  function handleChange(e) {
    const root = scrollRoot?.current;
    const top = root?.scrollTop;

    setQuery(e.target.value);
    if (!root || top == null) return;

    let frames = 4;
    const restore = () => {
      if (root.scrollTop !== top) root.scrollTo({ top, behavior: 'auto' });
      if (--frames > 0) requestAnimationFrame(restore);
    };
    restore();
  }

  function clip(text, max = 130) {
    if (text.length <= max) return text;
    return `${text.slice(0, max).trimEnd()}...`;
  }

  function handleSelect() {
    // Section chosen: close the panel and clear any query. The hash link does the scroll.
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setQuery('');
      setOpen(false);
      e.currentTarget.blur();
    }
  }

  // Close only when focus leaves the whole component (clicking a result keeps focus inside).
  function handleBlur(e) {
    if (!wrapRef.current?.contains(e.relatedTarget)) setOpen(false);
  }

  return (
    <div
      className={`${styles.wrap} ${compact ? styles.compactWrap : ''}`}
      ref={wrapRef}
      onBlur={handleBlur}
      role="search"
    >
      {/* A label, not a div, so tapping anywhere on the field focuses the input. That is what
          makes the icon-only mobile variant work without any JS. */}
      <label className={styles.field}>
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.2-3.2" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          placeholder={compact ? 'Search...' : 'Search Help Center...'}
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label="Search the Help Center"
          aria-controls={panelId}
          aria-expanded={open}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </label>

      {open && (
        <div id={panelId} className={styles.panel} aria-label="Help Center sections">
          {isSearching && results.length === 0 ? (
            <p className={styles.empty} aria-live="polite">No guides found.</p>
          ) : (
            <>
              <span className={styles.count} aria-live="polite">
                {isSearching
                  ? `${results.length} ${results.length === 1 ? 'guide' : 'guides'}`
                  : 'Jump to a section'}
              </span>
              <ul className={styles.list}>
                {results.map((r) => (
                  <li key={r.id}>
                    <a
                      className={styles.result}
                      href={`#${r.id}`}
                      onMouseDown={(e) => e.preventDefault()} // keep input focus so the click lands
                      onClick={handleSelect}
                    >
                      <span className={styles.resultTop}>
                        <span className={styles.resultTitle}>{r.title}</span>
                        <span className={styles.resultTag}>{r.tag}</span>
                      </span>
                      <span className={styles.resultSnippet}>{clip(r.snippet)}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
